/**
 * useCalendarViewGate
 *
 * Decides whether the Resource Dashboard may offer a MONTHLY view at all, and which view it
 * opens on. This is a direct translation of the legacy AngularJS logic; getting it wrong shows
 * users a view their population is not supposed to have.
 *
 * Legacy (shared/controllers/calendar.controller.js):
 *
 *   :117      ceViewingCal = NOT (alias in the route && alias !== me)   // i.e. SELF view only
 *   :121-160  enableCESYearlyView = IsCESCalendarGA
 *                                     ? CESCalendarEnabledForRoleId
 *                                     : IsCesRingUser
 *   :384-388  businessRoleId === 159            -> force YEARLY
 *   :397-399  enableCESYearlyView               -> force YEARLY (preference NOT read)
 *   :401-412  otherwise                         -> GetDefaultCalendarMode, default MONTHLY
 *
 * and shared/templates/calendardashboardpartial.html:170
 *   the Monthly View toggle is rendered with ng-if="!enableCESYearlyView"
 *   -> in LEGACY a CES-enabled user has no way to reach monthly from the Resource Dashboard.
 *
 * ⚠ DELIBERATE DIVERGENCE (@avytla, 2026-08-10): the React Resource Dashboard offers the SAME
 * monthly view to BOTH populations, so `canUseMonthly` is no longer gated on the CES flags.
 * `enableCESYearlyView` is still resolved because it drives the "Back to new calendar" link -
 * only the CES population has a /cescalendar to go back to (an ISD user is bounced straight out
 * of it by ce-calendar.component.ts:259-261,308-323).
 *
 * The CES month grid stays reachable: a user who arrived from /cescalendar is routed back there
 * by the entry-source marker consumed in YearlyCalendarPage.switchToMonthly.
 *
 * The CES gate applies to the SELF view only. A manager viewing someone else's calendar keeps the
 * monthly view regardless of their own CES flags, exactly as legacy does.
 */
import { useEffect, useRef, useState } from 'react';
import { CalendarMode } from '../../../contracts/YearlyCalendarContracts';
import { FlightingService } from '../../../services/Flighting.Service';
import YearlyCalendarBridgeService from '../../../services/YearlyCalendarBridge.service';

/** Legacy preference key (calendar.controller.js:71). */
export const DEFAULT_CALENDAR_MODE_PREFERENCE = 'DefaultCalendarMode';

/**
 * Legacy preference key written by `openNewDashboard` (calendar.controller.js:1509,1515).
 * Only consumer is the deprecated Manage Bulk Schedules page
 * (resourcedashboardv2/controllers/resource-dashboard.controller.js:156-171).
 */
export const NEW_CALENDAR_EXPERIENCE_PREFERENCE = 'NewCalendarExperiencePreferred';

/** Business role that is forced to the yearly view (calendar.controller.js:384-388). */
export const FORCED_YEARLY_BUSINESS_ROLE_ID = 159;

export const MONTHLY = 'MONTHLY';
export const YEARLY = 'YEARLY';

export interface UseCalendarViewGateOptions {
  /** Alias whose calendar is being shown. */
  targetAlias: string;
  /** True when a manager is viewing someone else's calendar. */
  isViewingOtherUser: boolean;
  /** Business role id of the signed-in user, from FXP UserInfoService.getCurrentUserData(). */
  businessRoleId?: number;
  /** Escape hatch for tests. */
  flightingService?: Pick<FlightingService, 'evaluateFeatureFlag'>;
  bridgeService?: {
    getUserPreference: (alias: string, type: string) => Promise<string | null>;
  };
}

export interface UseCalendarViewGateResult {
  /** False until the flags (and preference, when applicable) have been resolved. */
  resolved: boolean;
  /**
   * Whether the Monthly view may be offered. Now true for both populations; kept as a field so
   * cross-user/role rules and any future gating stay expressible in one place.
   */
  canUseMonthly: boolean;
  /** The view the page should open on once resolved. */
  defaultMode: CalendarMode;
  /** Raw gate value, exposed for telemetry/diagnostics. */
  enableCESYearlyView: boolean;
}

const readFlag = (response: any, name: string): boolean =>
  !!(response && typeof response === 'object' && response[name]);

export const useCalendarViewGate = (
  options: UseCalendarViewGateOptions
): UseCalendarViewGateResult => {
  const {
    targetAlias,
    isViewingOtherUser,
    businessRoleId,
    flightingService,
    bridgeService,
  } = options;

  const [state, setState] = useState<UseCalendarViewGateResult>({
    resolved: false,
    // Monthly is available to every population now, so there is no restrictive answer to assume
    // while the flags are in flight. Still starts unresolved so the page does not apply a default
    // view (or render the CES-only link) before the preference and flags land.
    canUseMonthly: true,
    defaultMode: 'richcc-year',
    enableCESYearlyView: false,
  });

  // Avoid applying a late async result after the component has moved on.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const resolve = async () => {
      // Cross-user: the CES gate does not apply (legacy :117 restricts it to the self view).
      // The Monthly control stays available, but for a manager it is a LINK OUT to the legacy
      // editable monthly page (grm-resourcedashboardquicksearch), not an in-page mode — so this
      // page still opens on the yearly view.
      if (isViewingOtherUser) {
        if (!cancelledRef.current) {
          setState({
            resolved: true,
            canUseMonthly: true,
            defaultMode: 'richcc-year',
            enableCESYearlyView: false,
          });
        }
        return;
      }

      let enableCESYearlyView = false;
      let bridge: { getUserPreference: (a: string, t: string) => Promise<string | null> } | null = null;
      try {
        const flighting = flightingService || FlightingService.getInstance();
        bridge = bridgeService || YearlyCalendarBridgeService.getSingleton();

        // Legacy issues all three in parallel (Promise.all at :125) and then branches on GA.
        const [gaResponse, roleResponse, ringResponse] = await Promise.all([
          flighting.evaluateFeatureFlag(['IsCESCalendarGA']),
          flighting.evaluateFeatureFlag(['CESCalendarEnabledForRoleId'], {
            BusinessRoleId: businessRoleId,
          }),
          flighting.evaluateFeatureFlag(['IsCesRingUser']),
        ]);

        enableCESYearlyView = readFlag(gaResponse, 'IsCESCalendarGA')
          ? readFlag(roleResponse, 'CESCalendarEnabledForRoleId')
          : readFlag(ringResponse, 'IsCesRingUser');
      } catch {
        // Flag evaluation (or service construction) failed. Legacy leaves enableCESYearlyView at
        // its initial false (:112), which keeps the monthly view available.
        enableCESYearlyView = false;
      }

      // NOTE: legacy hides Monthly for the CES population entirely
      // (calendardashboardpartial.html:170, `ng-if="!enableCESYearlyView"`) and forces yearly
      // without ever reading the preference (:397-399). We deliberately diverge: the Resource
      // Dashboard offers the SAME monthly view, and the same default-view behaviour, to both
      // populations (@avytla, 2026-08-10). Keeping CES on a forced yearly default would also be
      // incoherent now, since toggling writes DefaultCalendarMode that nothing would ever read.
      //
      // `enableCESYearlyView` is still resolved: it drives the "Back to new calendar" link, which
      // only the CES population can use (ISD users have no /cescalendar).
      //
      // This does NOT make the CES month grid unreachable — a user who arrived from /cescalendar
      // is routed back there by the entry-source marker in YearlyCalendarPage.switchToMonthly.

      // Role 159 forces yearly regardless of population (calendar.controller.js:384-388).
      if (businessRoleId === FORCED_YEARLY_BUSINESS_ROLE_ID) {
        if (!cancelledRef.current) {
          setState({
            resolved: true,
            canUseMonthly: true,
            defaultMode: 'richcc-year',
            enableCESYearlyView,
          });
        }
        return;
      }

      // Otherwise the saved preference decides, defaulting to MONTHLY on null/invalid/error.
      let preference: string | null = null;
      try {
        const source = bridge || bridgeService || YearlyCalendarBridgeService.getSingleton();
        preference = await source.getUserPreference(targetAlias, DEFAULT_CALENDAR_MODE_PREFERENCE);
      } catch {
        preference = null;
      }
      const normalized = (preference || '').toUpperCase();
      const defaultMode: CalendarMode = normalized === YEARLY ? 'richcc-year' : 'day';

      if (!cancelledRef.current) {
        setState({
          resolved: true,
          canUseMonthly: true,
          defaultMode,
          enableCESYearlyView,
        });
      }
    };

    resolve();

    return () => {
      cancelledRef.current = true;
    };
  }, [targetAlias, isViewingOtherUser, businessRoleId, flightingService, bridgeService]);

  return state;
};

export default useCalendarViewGate;
